/**
 * `dynamicTypeSize.isAccessibilitySize` for the web (spec §8.1).
 *
 * This is the single most load-bearing conditional in the product: it collapses
 * every multi-pane composition, and it is a **structural** branch rather than a
 * size branch, so `rem` scaling cannot stand in for it. It resolves in two
 * steps, in this order:
 *
 * 1. The explicit user setting (`TextSizePreference`). An explicit choice always
 *    wins, because the whole reason the setting exists is that the web has no
 *    trustworthy equivalent of the platform signal.
 * 2. A default-font-size heuristic, for users who set a large font in the
 *    browser and never open the app's settings.
 *
 * The heuristic is deliberately built out of `matchMedia` alone. In a media
 * query, `em` resolves against the browser's *initial* font size — the user's
 * font-size setting — and not against the root element's computed size. That is
 * exactly the signal wanted, and it means page zoom (which scales `px` and `em`
 * together) correctly does **not** trigger the structural branch: zoom is a
 * scale change, and this branch is not about scale.
 */

import type { MatchMedia, MediaProbeResult } from "./mediaQuery";
import { useMediaProbe } from "./mediaQuery";
import { useLayoutPreferences } from "./preferences";

/**
 * Apple's first accessibility size (AX1) sits at roughly 1.5x the default body
 * size, and 16 px is the browser default, so 24 px is where "large text" stops
 * being a scale preference and starts being a structural one.
 */
export const ACCESSIBILITY_ROOT_FONT_SIZE_PX = 24;

/**
 * The probe answers "is the default font size strictly greater than T?", so T
 * is nudged a hundredth below the threshold to make the documented value itself
 * qualify.
 */
const PROBE_EPSILON_PX = 0.01;

/** Above any plausible CSS-px viewport, and it only costs one more bisection step. */
const MAX_PROBE_WIDTH_PX = 8192;

/**
 * Largest whole-px width the viewport is known to be at least. Bisection rather
 * than a linear walk keeps the probe at ~14 `matchMedia` calls, which is what
 * makes it affordable to re-run on every environment change.
 */
function measureViewportWidth(matchMedia: MatchMedia): number {
  if (!matchMedia("(min-width: 0px)").matches) {
    // No environment answering width queries at all (a bare test stub, a
    // non-browser render). Reporting 0 makes the caller fall back rather than
    // invent a font size from a meaningless ratio.
    return 0;
  }
  let low = 0;
  let high = MAX_PROBE_WIDTH_PX;
  while (low < high) {
    const middle = Math.ceil((low + high) / 2);
    if (matchMedia(`(min-width: ${middle}px)`).matches) {
      low = middle;
    } else {
      high = middle - 1;
    }
  }
  return low;
}

/**
 * `(min-width: E em)` matches iff `viewport >= E * defaultFontSize`. Choosing
 * `E = viewport / T` reduces that to `T >= defaultFontSize`, so a **failure** to
 * match is a font larger than T. No division by the measured font size is ever
 * performed, which keeps the answer exact rather than accumulating the error of
 * an estimated font size.
 */
export function probeAccessibilityTextSize(
  matchMedia: MatchMedia,
): MediaProbeResult<boolean> {
  const viewportWidth = measureViewportWidth(matchMedia);
  if (viewportWidth <= 0) return { value: false, queries: [] };

  const emThreshold = viewportWidth / (ACCESSIBILITY_ROOT_FONT_SIZE_PX - PROBE_EPSILON_PX);
  const emQuery = `(min-width: ${emThreshold}em)`;
  const value = !matchMedia(emQuery).matches;

  return {
    value,
    // The em query is derived from the measured viewport, so a resize invalidates
    // it. The two px queries bracket the current width by one pixel in each
    // direction: whichever way the viewport moves, one of them flips and the
    // probe re-runs against the new width.
    queries: [emQuery, `(min-width: ${viewportWidth}px)`, `(min-width: ${viewportWidth + 1}px)`],
  };
}

/**
 * The §8.1 branch. Every consumer that changes *structure* at accessibility
 * text sizes reads this, and nothing re-derives it from a media query — a
 * re-derivation would miss the explicit setting, which is the half of the
 * signal the platform cannot give us.
 */
export function useAccessibilityTextSize(): boolean {
  const { textSize } = useLayoutPreferences();
  const detected = useMediaProbe(probeAccessibilityTextSize, false);
  return textSize === "accessibility" ? true : detected;
}
